import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { DocumentLink } from "@/components/DocumentLink";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { GraduationCap, Clock, Users, BookOpen, UserPlus, Plus, FileText, ExternalLink, ChevronLeft, ChevronRight, Folder } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { DonationForm } from "@/components/DonationForm";
import { EnrollmentForm } from "@/components/EnrollmentForm";
import { useSiteContent } from "@/hooks/useSiteContent";
import { BibleSchoolGalleryDisplay } from "@/components/BibleSchoolGalleryDisplay";

interface Course {
  id: string;
  title: string;
  description: string;
  instructor: string;
  duration: string;
  schedule: string | null;
  level: string;
  image_url: string | null;
  max_students: number | null;
  documents?: CourseDocument[];
}

interface CourseDocument {
  id: string;
  title: string;
  description: string | null;
  file_path: string;
  file_size: number | null;
  folder_name: string | null;
  folder_display_order: number;
}

const BibleSchool = () => {
  const languageContext = useLanguage();
  const authContext = useAuth();
  
  if (!languageContext || !authContext) {
    return <div>Loading...</div>;
  }
  
  const { t, language } = languageContext;
  const { isBibleSchoolEditor, isAdmin } = authContext;
  const { toast } = useToast();
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const { content: welcomeContent, isLoading: isLoadingWelcome } = useSiteContent('bibleschool-welcome');
  const { content: enrollmentContent, isLoading: isLoadingEnrollment } = useSiteContent('bibleschool-enrollment');
  
  const canAddContent = isBibleSchoolEditor || isAdmin;
  const coursesPerPage = 2;
  const totalPages = Math.ceil(courses.length / coursesPerPage);
  const startIndex = (currentPage - 1) * coursesPerPage;
  const endIndex = startIndex + coursesPerPage;
  const displayedCourses = courses.slice(startIndex, endIndex);
  const showPagination = courses.length > coursesPerPage;

  useEffect(() => {
    fetchCourses();
  }, [language]);

  const fetchCourses = async () => {
    try {
      setIsLoading(true);
      
      const { data: coursesData, error } = await supabase
        .from('courses')
        .select(`
          *,
          courses_translations (*),
          course_documents (
            id,
            title,
            description,
            file_path,
            file_size,
            folder_name,
            folder_display_order
          )
        `)
        .eq('published', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedCourses = coursesData?.map((course: any) => {
        const translation = course.courses_translations?.find(
          (t: any) => t.language === language
        );

        return {
          id: course.id,
          title: translation?.title || course.title,
          description: translation?.description || course.description,
          instructor: translation?.instructor || course.instructor,
          duration: translation?.duration || course.duration,
          schedule: translation?.schedule || course.schedule,
          level: translation?.level || course.level,
          image_url: course.image_url,
          max_students: course.max_students,
          documents: course.course_documents || [],
        };
      }) || [];

      setCourses(transformedCourses);
    } catch (error) {
      console.error("Error fetching courses:", error);
      toast({
        title: t("bibleschool.error"),
        description: t("bibleschool.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getDocumentUrl = async (filePath: string) => {
    const { data, error } = await supabase.storage
      .from('course-documents')
      .createSignedUrl(filePath, 3600); // 1 hour expiry
    return data?.signedUrl || '';
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto px-4 py-24">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-4 mb-4">
              <h1 className="text-4xl font-bold flex items-center gap-2">
                <GraduationCap className="h-8 w-8" />
                {t("bibleschool.title")}
              </h1>
            {canAddContent && (
              <Link to="/admin?tab=courses&action=add">
                <Button size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  {t("bibleschool.addCourse")}
                </Button>
              </Link>
            )}
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("bibleschool.subtitle")}
            </p>
          </div>

          {/* Introduction */}
          <Card className="mb-12">
            <CardHeader className="text-center pb-8">
              <CardTitle className="text-4xl md:text-5xl font-bold mb-4">
                {welcomeContent.title || t("bibleschool.welcome.title")}
              </CardTitle>
              {welcomeContent.subtitle && (
                <p className="text-xl text-muted-foreground">
                  {welcomeContent.subtitle}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-6 text-muted-foreground text-lg max-w-4xl mx-auto">
              {welcomeContent.text1 && <p className="whitespace-pre-line">{welcomeContent.text1}</p>}
              {welcomeContent.text2 && <p className="whitespace-pre-line">{welcomeContent.text2}</p>}
              {!welcomeContent.text1 && !welcomeContent.text2 && (
                <>
                  <p>{t("bibleschool.welcome.text1")}</p>
                  <p>{t("bibleschool.welcome.text2")}</p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Courses Grid */}
          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t("bibleschool.loading")}</p>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-12">
              <GraduationCap className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">{t("bibleschool.noCourses")}</p>
            </div>
          ) : (
            <>
              <div className={`grid gap-6 ${courses.length === 1 ? 'grid-cols-1' : 'md:grid-cols-2'}`}>
                {displayedCourses.map((course) => (
                <Card key={course.id} className="hover:shadow-lg transition-shadow">
                  {course.image_url && (
                    <div className="aspect-video overflow-hidden rounded-t-lg">
                      <img
                        src={course.image_url}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary">{course.level}</Badge>
                      {course.max_students && (
                        <Badge variant="outline">
                          <Users className="h-3 w-3 mr-1" />
                          {t("bibleschool.maxStudents")}: {course.max_students}
                        </Badge>
                      )}
                    </div>
                    <CardTitle>{course.title}</CardTitle>
                    <CardDescription className="whitespace-pre-line">{course.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <BookOpen className="h-4 w-4" />
                        <span>{t("bibleschool.instructor")}: {course.instructor}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{t("bibleschool.duration")}: {course.duration}</span>
                      </div>
                      {course.schedule && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-4 w-4" />
                          <span>{t("bibleschool.schedule")}: {course.schedule}</span>
                        </div>
                      )}
                    </div>

                    {course.documents && course.documents.length > 0 && (
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          {language === 'en' ? 'Course Documents' : language === 'ru' ? 'Материалы курса' : 'Documentos del Curso'}
                        </h4>
                        {(() => {
                          // Group documents by folder
                          const grouped = course.documents.reduce((acc, doc) => {
                            const folder = doc.folder_name || (language === 'en' ? 'General' : language === 'ru' ? 'Общие' : 'General');
                            if (!acc[folder]) acc[folder] = [];
                            acc[folder].push(doc);
                            return acc;
                          }, {} as Record<string, CourseDocument[]>);

                          const folders = Object.entries(grouped);

                          // If only one folder or all documents in "General", show flat list
                          if (folders.length === 1 && (folders[0][0] === 'General' || folders[0][0] === 'Общие')) {
                            return (
                              <div className="space-y-2">
                                {folders[0][1].map((doc) => (
                                  <DocumentLink
                                    key={doc.id}
                                    bucket="course-documents"
                                    filePath={doc.file_path}
                                    className="flex items-center gap-2 text-sm text-primary hover:underline p-2 hover:bg-muted rounded-md transition-colors"
                                  >
                                    <FileText className="h-4 w-4 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium truncate">{doc.title}</div>
                                      {doc.description && (
                                        <div className="text-xs text-muted-foreground truncate">{doc.description}</div>
                                      )}
                                    </div>
                                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                  </DocumentLink>
                                ))}
                              </div>
                            );
                          }

                          // Multiple folders: show accordion
                          return (
                            <Accordion type="multiple" defaultValue={folders.map((_, i) => `folder-${i}`)} className="w-full">
                              {folders.map(([folder, docs], index) => (
                                <AccordionItem key={`folder-${index}`} value={`folder-${index}`}>
                                  <AccordionTrigger className="text-sm hover:no-underline">
                                    <div className="flex items-center gap-2">
                                      <Folder className="h-4 w-4 text-primary" />
                                      <span className="font-medium">{folder}</span>
                                      <Badge variant="secondary" className="ml-2">
                                        {docs.length}
                                      </Badge>
                                    </div>
                                  </AccordionTrigger>
                                  <AccordionContent>
                                    <div className="space-y-1 pl-6">
                                      {docs.map((doc) => (
                                        <DocumentLink
                                          key={doc.id}
                                          bucket="course-documents"
                                          filePath={doc.file_path}
                                          className="flex items-center gap-2 text-sm text-primary hover:underline p-2 hover:bg-muted rounded-md transition-colors"
                                        >
                                          <FileText className="h-4 w-4 flex-shrink-0" />
                                          <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">{doc.title}</div>
                                            {doc.description && (
                                              <div className="text-xs text-muted-foreground truncate">{doc.description}</div>
                                            )}
                                          </div>
                                          <ExternalLink className="h-3 w-3 flex-shrink-0" />
                                        </DocumentLink>
                                      ))}
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              ))}
                            </Accordion>
                          );
                        })()}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {showPagination && (
              <div className="flex items-center justify-center gap-2 mt-8 animate-fade-in">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  {language === 'en' ? 'Previous' : language === 'ru' ? 'Назад' : 'Anterior'}
                </Button>
                
                <div className="flex items-center gap-2">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className="min-w-[2.5rem]"
                    >
                      {page}
                    </Button>
                  ))}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="gap-1"
                >
                  {language === 'en' ? 'Next' : language === 'ru' ? 'Вперед' : 'Siguiente'}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
          )}

          {/* Photo and Video Gallery */}
          <BibleSchoolGalleryDisplay />

          {/* Enrollment Contact Section */}
          <Card className="mt-12">
            <CardHeader>
              <CardTitle>{enrollmentContent.title || t("bibleschool.contact.title")}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-6">{enrollmentContent.description || "Fill out the form below to inquire about enrolling in our Bible School program."}</p>
              <EnrollmentForm />
            </CardContent>
          </Card>

          {/* Become a Contributor CTA - Only show if not already a contributor */}
          {!canAddContent && (
            <Card className="mt-12 bg-primary/5 border-primary/20">
              <CardHeader className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-primary/10 rounded-full">
                    <UserPlus className="h-8 w-8 text-primary" />
                  </div>
                </div>
                <CardTitle className="text-2xl">{t("bibleschool.contribute.title")}</CardTitle>
                <CardDescription className="text-base">
                  {t("bibleschool.contribute.description")}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex justify-center pb-6">
                <Link to="/become-contributor">
                  <Button size="lg" className="gap-2">
                    <UserPlus className="h-5 w-5" />
                    {t("bibleschool.contribute.button")}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Donation Section */}
          <div className="mt-12">
            <DonationForm defaultCategory="bible-school" />
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BibleSchool;
